import { assign } from 'lodash';
import { variables } from '../utils';
import BinaryStackedChart from './BinaryStackedChart';
import ChartCard from './ChartCard';
import ChartContainer from './ChartContainer';
import { SubjectChartList, TrackerChartList } from './ChartList';
import FiverChart from './FiverChart';
import NumberChart from './NumberChart';
import TrafficLightStackedChart from './TrafficLightStackedChart';

// *
// * Colors
// *
const colors = ['#252525', '#525252', '#737373', '#969696', '#bdbdbd', '#d9d9d9', '#f0f0f0'];

const blue = variables.blue;
const darkGray = variables.navy;
const tan = variables.tan;

// *
// * Typography
// *
const sansSerif = '"Arquette", sans-serif';
const letterSpacing = '.05em';
const fontSize = 9;
// *
// * Layout
// *
const baseProps = {
  width: 450,
  height: 400,
  padding: 50,
  colorScale: colors
};
// *
// * Labels
// *
const baseLabelStyles = {
  fontFamily: sansSerif,
  fontSize,
  textTransform: 'uppercase',
  letterSpacing,
  padding: 10,
  fill: tan,
  stroke: 'transparent'
};

const centeredLabelStyles = assign({ textAnchor: 'middle' }, baseLabelStyles);
// *
// * Strokes
// *
const strokeLinecap = 'round';
const strokeLinejoin = 'round';

const QualaVictoryTheme: any = {
  area: assign(
    {
      style: {
        data: {
          fill: darkGray
        },
        labels: centeredLabelStyles
      }
    },
    baseProps
  ),
  axis: assign(
    {
      style: {
        axis: {
          fill: 'transparent',
          stroke: variables.black10,
          strokeWidth: 1,
          strokeLinecap,
          strokeLinejoin
        },
        axisLabel: assign({}, centeredLabelStyles, {
          padding: 25
        }),
        grid: {
          fill: 'none',
          stroke: variables.black10,
          pointerEvents: 'none'
        },
        ticks: {
          fill: 'transparent',
          size: 1,
          stroke: 'transparent'
        },
        tickLabels: baseLabelStyles
      }
    },
    baseProps
  ),
  bar: assign(
    {
      barWidth: 4,
      style: {
        data: {
          fill: variables.navy,
          padding: 0,
          strokeWidth: 0
        },
        labels: baseLabelStyles
      }
    },
    baseProps
  ),
  boxplot: assign(
    {
      style: {
        max: { padding: 8, stroke: darkGray, strokeWidth: 1 },
        maxLabels: baseLabelStyles,
        median: { padding: 8, stroke: darkGray, strokeWidth: 1 },
        medianLabels: baseLabelStyles,
        min: { padding: 8, stroke: darkGray, strokeWidth: 1 },
        minLabels: baseLabelStyles,
        q1: { padding: 8, fill: blue },
        q1Labels: baseLabelStyles,
        q3: { padding: 8, fill: blue },
        q3Labels: baseLabelStyles
      },
      boxWidth: 20
    },
    baseProps
  ),
  candlestick: assign(
    {
      style: {
        data: {
          stroke: darkGray,
          strokeWidth: 1
        },
        labels: centeredLabelStyles
      },
      candleColors: {
        positive: '#ffffff',
        negative: darkGray
      }
    },
    baseProps
  ),
  chart: baseProps,
  errorbar: assign(
    {
      borderWidth: 8,
      style: {
        data: {
          fill: 'transparent',
          stroke: darkGray,
          strokeWidth: 2
        },
        labels: centeredLabelStyles
      }
    },
    baseProps
  ),
  group: assign(
    {
      colorScale: colors
    },
    baseProps
  ),
  legend: {
    colorScale: colors,
    gutter: 10,
    orientation: 'vertical',
    titleOrientation: 'top',
    style: {
      data: {
        type: 'circle'
      },
      labels: baseLabelStyles,
      title: assign({}, baseLabelStyles, { padding: 5 })
    }
  },
  line: assign(
    {
      style: {
        data: {
          fill: 'transparent',
          stroke: blue,
          strokeWidth: 2
        },
        labels: centeredLabelStyles
      }
    },
    baseProps
  ),
  pie: {
    style: {
      data: {
        padding: 10,
        stroke: 'transparent',
        strokeWidth: 1
      },
      labels: assign({}, baseLabelStyles, { padding: 20 })
    },
    colorScale: colors,
    width: 400,
    height: 400,
    padding: 50
  },
  scatter: assign(
    {
      style: {
        data: {
          fill: blue,
          stroke: 'transparent',
          strokeWidth: 0
        },
        labels: centeredLabelStyles
      }
    },
    baseProps
  ),
  stack: assign(
    {
      colorScale: colors
    },
    baseProps
  ),
  tooltip: {
    style: assign({}, centeredLabelStyles, { padding: 5, pointerEvents: 'none' }),
    flyoutStyle: {
      stroke: darkGray,
      strokeWidth: 1,
      fill: '#f0f0f0',
      pointerEvents: 'none'
    },
    cornerRadius: 5,
    pointerLength: 10
  },
  voronoi: assign(
    {
      style: {
        data: {
          fill: 'transparent',
          stroke: 'transparent',
          strokeWidth: 0
        },
        labels: assign({}, centeredLabelStyles, { padding: 5, pointerEvents: 'none' }),
        flyout: {
          stroke: darkGray,
          strokeWidth: 1,
          fill: '#f0f0f0',
          pointerEvents: 'none'
        }
      }
    },
    baseProps
  )
};

export {
  QualaVictoryTheme,
  BinaryStackedChart,
  FiverChart,
  NumberChart,
  TrafficLightStackedChart,
  ChartCard,
  SubjectChartList,
  TrackerChartList,
  ChartContainer
};
